import './MenuItem.scss';

import { CaretDown } from '@phosphor-icons/react';
import classNames from 'classnames';
import { memo, useState } from 'react';

import { NavItem } from '../../interfaces/NavBar';

type MenuItemProps = {
  item: NavItem;
  handleNavItemClick: (item: NavItem) => void;
  handleSubMenuClose: () => void;
  variant: 'nav-bar' | 'contact-bar';
};

const className = 'c-EcommerceMenuItem';

const SubMenuItem = ({
  item,
  handleNavItemClick,
  handleSubMenuClose,
}: MenuItemProps) => {
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const handleFocus = () => setIsFocused(true);
  const handleBlur = () => {
    setIsFocused(false);
    handleSubMenuClose();
  };

  return (
    <div
      tabIndex={0}
      className={classNames(`${className}__submenu-item`, {
        [`${className}__submenu-item--focused`]: isFocused,
      })}
      onMouseEnter={() => handleFocus()}
      onFocus={() => handleFocus()}
      onMouseLeave={() => handleBlur()}
      onBlur={() => handleBlur()}
      onClick={() => handleNavItemClick(item)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          handleNavItemClick(item);
        }
      }}
    >
      {item.label}
    </div>
  );
};

const MenuItem = ({
  item,
  variant,
  handleNavItemClick,
  handleSubMenuClose,
}: MenuItemProps) => {
  const [isFocused, setIsFocused] = useState<boolean>(false);

  const handleFocus = () => setIsFocused(true);
  const handleBlur = () => {
    setIsFocused(false);
    handleSubMenuClose();
  };

  const renderMenuSubItems = (items: Array<NavItem>) => {
    return (
      <div className={`${className}__menu-item-children`}>
        {items.map((item: NavItem) => {
          if (!item.visible) return null;

          return (
            <SubMenuItem
              key={item.id}
              item={item}
              variant={variant}
              handleNavItemClick={handleNavItemClick}
              handleSubMenuClose={handleSubMenuClose}
            />
          );
        })}
      </div>
    );
  };

  return (
    <div
      className={classNames(`${className}__menu-item`, {
        [`${className}__menu-item--focused`]: isFocused,
      })}
      onMouseEnter={() => handleFocus()}
      onFocus={() => handleFocus()}
      onMouseLeave={() => handleBlur()}
      onBlur={() => handleBlur()}
    >
      <button
        type="button"
        className={classNames(
          `${className}__menu-item-link`,
          `${className}__menu-item-link--${variant}`,
          {
            [`${className}__menu-item-link--focused`]: isFocused,
            [`${className}__menu-item-link--${variant}--focused`]:
              isFocused && variant,
          },
        )}
        data-testid={item['data-testid']}
        onClick={() => handleNavItemClick(item)}
      >
        {item.icon && (
          <span className={`${className}__menu-item-link-icon`}>
            {item.icon}
          </span>
        )}

        {item.label}
        {Array.isArray(item.items) && item.items.length > 0 ? (
          <CaretDown
            size={16}
            weight="bold"
            className={`${className}__menu-item-link-caret`}
          />
        ) : null}
      </button>
      {Array.isArray(item.items) &&
        item.items.length > 0 &&
        renderMenuSubItems(item.items)}
    </div>
  );
};

export default memo(MenuItem);
